import Box from "@mui/material/Box";

import APDetailList from "../../components/APDetailList";
import { formatDate } from "../../helpers/dates";
import { AppLocationRenderFunction } from "../../helpers/faq";
import {
  DossierTermination,
  DossierTerminationReasonLabelMap,
} from "../../types/Dossier";
import { CRMAppLocationIdentifier } from "../../types/Faq";

type DossierTerminationSummaryFields = Partial<
  Pick<
    DossierTermination,
    | "leavingEmployer"
    | "lastDateOfEmployment"
    | "targetLastPayrollDate"
    | "endDate"
    | "terminationReason"
    | "driverNotifiedOfTermination"
    | "requestedByContactName"
    | "requestedAt"
    | "noteFromEmployer"
    | "noteFromDriver"
  >
>;

const DossierTerminationSummary = ({
  dossierTermination,
  displayedFields,
  appLocations,
  labelOverrides,
  renderAppLocation,
  fieldActions,
}: {
  dossierTermination: DossierTerminationSummaryFields;
  displayedFields: (keyof DossierTerminationSummaryFields)[];
  appLocations?: Partial<
    Record<keyof DossierTerminationSummaryFields, CRMAppLocationIdentifier>
  >;
  labelOverrides?: Partial<
    Record<keyof DossierTerminationSummaryFields, string>
  >;
  fieldActions?: Partial<
    Record<keyof DossierTerminationSummaryFields, React.ReactNode>
  >;
  renderAppLocation?: AppLocationRenderFunction;
}) => {
  return (
    <Box>
      <APDetailList
        items={[
          ...(displayedFields.includes("leavingEmployer") &&
          dossierTermination.leavingEmployer !== undefined &&
          dossierTermination.leavingEmployer !== null
            ? [
                {
                  label: labelOverrides?.leavingEmployer || "Leaving Employer",
                  value: dossierTermination.leavingEmployer ? "Yes" : "No",
                  appLocationIdentifier: appLocations?.leavingEmployer,
                  itemAction: fieldActions?.leavingEmployer,
                },
              ]
            : []),
          ...(displayedFields.includes("terminationReason") &&
          dossierTermination.terminationReason
            ? [
                {
                  label:
                    labelOverrides?.terminationReason || "Termination Reason",
                  value:
                    DossierTerminationReasonLabelMap[
                      dossierTermination.terminationReason
                    ],
                  appLocationIdentifier: appLocations?.terminationReason,
                  itemAction: fieldActions?.terminationReason,
                },
              ]
            : []),
          ...(displayedFields.includes("lastDateOfEmployment") &&
          dossierTermination.lastDateOfEmployment
            ? [
                {
                  label:
                    labelOverrides?.lastDateOfEmployment ||
                    "Last Date of Employment",
                  value: formatDate(dossierTermination.lastDateOfEmployment),
                  appLocationIdentifier: appLocations?.lastDateOfEmployment,
                  itemAction: fieldActions?.lastDateOfEmployment,
                },
              ]
            : []),
          ...(displayedFields.includes("targetLastPayrollDate") &&
          dossierTermination.targetLastPayrollDate
            ? [
                {
                  label:
                    labelOverrides?.targetLastPayrollDate ||
                    "Target Last Payroll Date",
                  value: formatDate(dossierTermination.targetLastPayrollDate),
                  appLocationIdentifier: appLocations?.targetLastPayrollDate,
                  itemAction: fieldActions?.targetLastPayrollDate,
                },
              ]
            : []),

          ...(displayedFields.includes("endDate") && dossierTermination.endDate
            ? [
                {
                  label: labelOverrides?.endDate || "End Date",
                  value: formatDate(dossierTermination.endDate),
                  appLocationIdentifier: appLocations?.endDate,
                  itemAction: fieldActions?.endDate,
                },
              ]
            : []),
          ...(displayedFields.includes("requestedByContactName") &&
          dossierTermination.requestedByContactName
            ? [
                {
                  label:
                    labelOverrides?.requestedByContactName || "Requested By",
                  value: `${dossierTermination.requestedByContactName} - ${
                    dossierTermination.requestedAt
                      ? formatDate(dossierTermination.requestedAt)
                      : ""
                  }`,
                  appLocationIdentifier: appLocations?.requestedByContactName,
                  itemAction: fieldActions?.requestedByContactName,
                },
              ]
            : []),
          ...(displayedFields.includes("driverNotifiedOfTermination") &&
          dossierTermination.driverNotifiedOfTermination !== undefined &&
          dossierTermination.driverNotifiedOfTermination !== null
            ? [
                {
                  label:
                    labelOverrides?.driverNotifiedOfTermination ||
                    "Employee Notified of Termination",
                  value: dossierTermination.driverNotifiedOfTermination
                    ? "Yes"
                    : "No",
                  appLocationIdentifier:
                    appLocations?.driverNotifiedOfTermination,
                  itemAction: fieldActions?.driverNotifiedOfTermination,
                },
              ]
            : []),
          ...(displayedFields.includes("noteFromEmployer") &&
          dossierTermination.noteFromEmployer
            ? [
                {
                  label: labelOverrides?.noteFromEmployer || "Note",
                  value: dossierTermination.noteFromEmployer,
                  appLocationIdentifier: appLocations?.noteFromEmployer,
                  itemAction: fieldActions?.noteFromEmployer,
                },
              ]
            : []),
          ...(displayedFields.includes("noteFromDriver") &&
          dossierTermination.noteFromDriver
            ? [
                {
                  label: labelOverrides?.noteFromDriver || "Note",
                  value: dossierTermination.noteFromDriver,
                  appLocationIdentifier: appLocations?.noteFromDriver,
                  itemAction: fieldActions?.noteFromDriver,
                },
              ]
            : []),
        ]}
        renderAppLocation={renderAppLocation}
      />
    </Box>
  );
};

export default DossierTerminationSummary;
